/**
 * Shared drawing kit for the flight art set: deterministic hashing, colour
 * helpers, path helpers and a bounded sprite cache. Everything here is purely
 * visual — nothing may feed back into combat randomness.
 */
export type CanvasFactory = (w: number, h: number) => HTMLCanvasElement;
type Ctx = CanvasRenderingContext2D;

/** Bold sticker outline shared by enemies, pickups and HUD glyphs. */
export const INK = "#1B2130";
export const FONT = '"PingFang SC","Microsoft YaHei",sans-serif';
export const NUM_FONT = '"DIN Alternate","Arial Rounded MT Bold","PingFang SC","Microsoft YaHei",sans-serif';
/** Sprites are rasterised at twice the logical size so phones stay crisp. */
const SPRITE_RES = 2;
const LIMIT = 260;

let factory: CanvasFactory | undefined;
const cache = new Map<string, HTMLCanvasElement>();

export function setArtCanvasFactory(next?: CanvasFactory) {
  if (next === factory) return;
  factory = next;
  cache.clear();
}
/** A fresh offscreen canvas, or null when no factory is registered. */
export function makeCanvas(w: number, h: number) {
  return factory ? factory(Math.max(1, Math.ceil(w)), Math.max(1, Math.ceil(h))) : null;
}
/** Evict cached canvases whose key starts with the prefix (e.g. an old biome). */
export function dropCached(prefix: string) {
  for (const key of [...cache.keys()]) if (key.startsWith(prefix)) cache.delete(key);
}

export function dropKey(key: string) {
  cache.delete(key);
}
export function isCached(key: string) {
  return cache.has(key);
}

/**
 * Returns a cached raster, or null when no canvas factory is registered
 * (unit tests paint straight onto the target instead).
 */
export function raster(
  key: string,
  w: number,
  h: number,
  res: number,
  draw: (c: Ctx) => void,
): HTMLCanvasElement | null {
  if (!factory) return null;
  let canvas = cache.get(key);
  if (canvas) {
    cache.delete(key);
    cache.set(key, canvas);
    return canvas;
  }
  canvas = factory(Math.max(1, Math.ceil(w * res)), Math.max(1, Math.ceil(h * res)));
  const c = canvas.getContext("2d")!;
  c.scale(res, res);
  draw(c);
  cache.set(key, canvas);
  if (cache.size > LIMIT) cache.delete(cache.keys().next().value!);
  return canvas;
}

/**
 * Draws a centred sprite whose drawing function uses (0,0) as the centre and
 * fits inside ±w/2, ±h/2. Falls back to direct drawing without a factory.
 */
export function stamp(
  c: Ctx,
  key: string,
  w: number,
  h: number,
  draw: (c: Ctx) => void,
  x = 0,
  y = 0,
  angle = 0,
  scale = 1,
) {
  const canvas = raster(key, w, h, SPRITE_RES, (g) => {
    g.translate(w / 2, h / 2);
    draw(g);
  });
  c.save();
  c.translate(x, y);
  if (angle) c.rotate(angle);
  if (scale !== 1) c.scale(scale, scale);
  if (canvas) c.drawImage(canvas, -w / 2, -h / 2, w, h);
  else draw(c);
  c.restore();
}

/** Solid-colour silhouette of a sprite (hit flash / cast shadow). */
export function silhouette(
  c: Ctx,
  key: string,
  w: number,
  h: number,
  draw: (c: Ctx) => void,
  color: string,
  x = 0,
  y = 0,
  angle = 0,
  scale = 1,
) {
  if (!factory) return;
  stamp(
    c,
    key + "#" + color,
    w,
    h,
    (g) => {
      draw(g);
      g.globalCompositeOperation = "source-in";
      g.fillStyle = color;
      g.fillRect(-w / 2, -h / 2, w, h);
    },
    x,
    y,
    angle,
    scale,
  );
}

export function hash(...values: number[]) {
  let h = 0x2f6b9c1d;
  for (const v of values) {
    h = Math.imul(h ^ (Math.round(v * 4096) | 0), 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
  }
  return (h >>> 0) / 4294967296;
}

function parse(hex: string) {
  const v = hex.replace("#", "");
  const n = parseInt(v.length === 3 ? v.replace(/./g, (x) => x + x) : v.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** Mix a colour toward black (amount < 0) or white (amount > 0). */
export function shade(hex: string, amount: number) {
  const [r, g, b] = parse(hex),
    t = amount < 0 ? 0 : 255,
    k = Math.abs(amount);
  const f = (x: number) => Math.round(x + (t - x) * k).toString(16).padStart(2, "0");
  return "#" + f(r) + f(g) + f(b);
}
export function mix(a: string, b: string, t: number) {
  const x = parse(a),
    y = parse(b);
  return (
    "#" +
    x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, "0")).join("")
  );
}
export function rgba(hex: string, alpha: number) {
  const [r, g, b] = parse(hex);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
}

export function trace(c: Ctx, pts: number[][]) {
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.closePath();
}
export function poly(
  c: Ctx,
  pts: number[][],
  fill: string | CanvasGradient,
  stroke: string | null = INK,
  width = 2.4,
) {
  trace(c, pts);
  c.fillStyle = fill;
  c.fill();
  if (stroke) {
    c.lineJoin = "round";
    c.strokeStyle = stroke;
    c.lineWidth = width;
    c.stroke();
  }
}
export function disc(
  c: Ctx,
  x: number,
  y: number,
  r: number,
  fill: string | CanvasGradient,
  stroke: string | null = null,
  width = 2.4,
) {
  c.beginPath();
  c.arc(x, y, Math.max(0.1, r), 0, Math.PI * 2);
  c.fillStyle = fill;
  c.fill();
  if (stroke) {
    c.strokeStyle = stroke;
    c.lineWidth = width;
    c.stroke();
  }
}
export function oval(
  c: Ctx,
  x: number,
  y: number,
  rx: number,
  ry: number,
  fill: string | CanvasGradient,
  stroke: string | null = null,
  width = 2.4,
  rotation = 0,
) {
  c.beginPath();
  c.ellipse(x, y, Math.max(0.1, rx), Math.max(0.1, ry), rotation, 0, Math.PI * 2);
  c.fillStyle = fill;
  c.fill();
  if (stroke) {
    c.strokeStyle = stroke;
    c.lineWidth = width;
    c.stroke();
  }
}
export function roundRect(c: Ctx, x: number, y: number, w: number, h: number, r: number) {
  const k = Math.max(0, Math.min(r, w / 2, h / 2));
  c.beginPath();
  c.moveTo(x + k, y);
  c.arcTo(x + w, y, x + w, y + h, k);
  c.arcTo(x + w, y + h, x, y + h, k);
  c.arcTo(x, y + h, x, y, k);
  c.arcTo(x, y, x + w, y, k);
  c.closePath();
}
export function box(
  c: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string | CanvasGradient,
  stroke: string | null = INK,
  width = 2.4,
) {
  roundRect(c, x, y, w, h, r);
  c.fillStyle = fill;
  c.fill();
  if (stroke) {
    c.strokeStyle = stroke;
    c.lineWidth = width;
    c.stroke();
  }
}
export function line(c: Ctx, pts: number[][], color: string, width: number, cap: CanvasLineCap = "round") {
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.strokeStyle = color;
  c.lineWidth = width;
  c.lineCap = cap;
  c.lineJoin = "round";
  c.stroke();
}
export function linear(c: Ctx, x0: number, y0: number, x1: number, y1: number, stops: [number, string][]) {
  const g = c.createLinearGradient(x0, y0, x1, y1);
  for (const [t, color] of stops) g.addColorStop(t, color);
  return g;
}
export function radial(c: Ctx, x: number, y: number, r0: number, r1: number, stops: [number, string][], fx = x, fy = y) {
  const g = c.createRadialGradient(fx, fy, r0, x, y, r1);
  for (const [t, color] of stops) g.addColorStop(t, color);
  return g;
}
let additive = true;
/**
 * Additive light only reads well on dark scenes; on bright terrain it washes
 * out to white, so light biomes blend glows normally at reduced strength.
 */
export function setGlowAdditive(value: boolean) {
  additive = value;
}
/** Soft glow disc, cached per colour. */
export function glow(c: Ctx, x: number, y: number, r: number, color: string, alpha = 1) {
  if (r <= 0 || alpha <= 0) return;
  const previous = c.globalCompositeOperation,
    a = c.globalAlpha;
  c.globalCompositeOperation = additive ? "lighter" : "source-over";
  c.globalAlpha = a * Math.min(1, alpha) * (additive ? 1 : 0.7);
  stamp(
    c,
    "glow:" + color,
    64,
    64,
    (g) => {
      g.fillStyle = radial(g, 0, 0, 0, 32, [
        [0, rgba(color, 0.9)],
        [0.35, rgba(color, 0.42)],
        [1, rgba(color, 0)],
      ]);
      g.fillRect(-32, -32, 64, 64);
    },
    x,
    y,
    0,
    r / 32,
  );
  c.globalAlpha = a;
  c.globalCompositeOperation = previous;
}
/** Irregular blob outline used for clouds, canopy, ink and rocks. */
export function blob(c: Ctx, x: number, y: number, r: number, seed: number, lobes = 7, wobble = 0.22, squash = 1) {
  c.beginPath();
  const steps = lobes * 4;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2,
      k = 1 + wobble * Math.sin(a * lobes + seed * 6.28) + wobble * 0.5 * Math.sin(a * (lobes + 3) + seed * 17);
    const px = x + Math.cos(a) * r * k,
      py = y + Math.sin(a) * r * k * squash;
    i ? c.lineTo(px, py) : c.moveTo(px, py);
  }
  c.closePath();
}
export function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}
export const TAU = Math.PI * 2;
